import icArrowLeft from "@/assets/icons/ic-arrow-left.svg";
import IconHelp from "@/assets/icons/icon-help.svg";
import { ConfirmDialog, Pagination } from "@/components/common";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TruncatedTooltip } from "@/components/ui/truncated-tooltip";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/lib/utils";
import { deleteAgent, getAgents } from "@/services/agent-service";
import type { Agent } from "@/types/agent";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const ITEMS_PER_PAGE = 10;

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  in_progress: {
    label: "진행 중",
    bg: "bg-[rgba(255,128,19,0.11)]",
    text: "text-[#FD5108]",
  },
  under_review: {
    label: "검토 중",
    bg: "bg-[#E0B244]",
    text: "text-[#FFFFFF]",
  },
  completed: {
    label: "완료",
    bg: "bg-[#43946C]",
    text: "text-[#FFFFFF]",
  },
  rejected: {
    label: "거부됨",
    bg: "bg-[#CB3A31]",
    text: "text-[#FFFFFF]",
  },
};

export default function AgentListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const fetchAgents = useCallback(async (page: number) => {
    setIsLoading(true);
    try {
      const result = await getAgents(page, ITEMS_PER_PAGE);
      setAgents(result.items);
      setTotal(result.total);
    } catch {
      setAgents([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents(currentPage);
  }, [currentPage, fetchAgents]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAgent(deleteTarget);
      toast({ description: "에이전트가 삭제되었습니다." });
      setDeleteTarget(null);
      if (agents.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchAgents(currentPage);
      }
    } catch (err) {
      toast({
        variant: "destructive",
        description: "삭제에 실패했습니다.",
      });
      setDeleteTarget(null);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.in_progress;
    const statusComplete = ["completed"];
    return (
      <div
        className={`inline-flex items-center justify-center w-fit gap-1 rounded  leading-6  tracking-[-0.01em] ${config.bg} ${config.text} ${statusComplete.includes(status) ? "px-4 " : " px-[6.5px] "}`}
      >
        <p>{config.label}</p>
      </div>
    );
  };

  const canEdit = (status: string) => status === "in_progress" || status === "under_review";

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div
        className="flex flex-col gap-6 px-5 py-6 lg:px-[160px] lg:py-10 max-w-[1440px] mx-auto w-full"
        style={{ display: isLoading ? "none" : "flex" }}
      >
        <div className="flex  gap-4 w-full lg:flex-row flex-col lg:items-center ">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/my-page")}
            className="w-12 h-12 rounded-full bg-black/[0.04] hover:bg-black/[0.08]"
          >
            <img src={icArrowLeft} alt="Icon Arrow Left" />
          </Button>
          <h1 className="flex-1 text-xl font-bold leading-7 tracking-[-0.02em] text-[#202020]">
            {user?.requester_name || user?.email?.split("@")[0] || "User"}님의 에이전트
          </h1>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate("/my-agents/new")}
              className="w-[120px] h-10 lg:h-12 rounded-full bg-[#FD5108] text-sm lg:text-base font-bold text-white "
            >
              등록
            </Button>
          </div>
        </div>

        {/* Mobile: Card layout */}
        <div className="flex flex-col gap-6 lg:hidden border border-black/[0.09] lg:border-none rounded-2xl lg:rounded-none pb-6 lg:pb-0">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className=" overflow-hidden cursor-pointer bg-[rgba(255,128,19,0.11)] rounded-2xl "
              onClick={() => navigate(`/my-agents/${agent.id}/detail`)}
            >
              <div className="px-3 py-4 ">
                <TruncatedTooltip className="block truncate text-base font-bold text-[rgba(0,0,0,0.91)]">
                  {agent.name || "-"}
                </TruncatedTooltip>
              </div>
              <div className="bg-white rounded-xl mx-1 mb-1 p-3 pb-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-base text-[#202020]">
                    {agent.created_at ? formatDate(agent.created_at) : "-"}
                  </span>
                  {getStatusBadge(agent.status)}
                </div>
                <div className="h-px bg-black/[0.09]" />
                <div
                  className="flex justify-end items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="ghost"
                    onClick={() => setDeleteTarget(agent.id)}
                    className="w-[80px] h-8 rounded-full bg-[rgba(245,43,0,0.09)] px-3 py-2 text-xs font-bold text-[#CF3716] hover:bg-[rgba(245,43,0,0.15)] transition-colors"
                  >
                    삭제
                  </Button>
                  {canEdit(agent.status) ? (
                    <Button
                      variant="ghost"
                      onClick={() => navigate(`/my-agents/${agent.id}/edit`)}
                      className="w-[80px] h-8 rounded-full border border-black/[0.15] bg-white/[0.01] px-3 py-2 text-xs font-bold text-[#202020] backdrop-blur-[40px] hover:bg-black/[0.04] transition-colors"
                    ></Button>
                  ) : (
                    <div className="w-[80px]" />
                  )}
                </div>
              </div>
            </div>
          ))}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>

        {/* Desktop: Table layout */}
        <div className="hidden lg:block w-full rounded-lg border border-black/[0.09] overflow-hidden">
          <Table>
            <TableHeader className="[&_tr]:border-b-0">
              <TableRow className="hover:bg-transparent">
                <TableHead className="bg-black/[0.04] text-xs font-normal uppercase text-[#8D8D8D] h-10 py-0 px-3 w-[373px] border-b border-black/[0.09]">
                  에이전트 명
                </TableHead>
                <TableHead className="bg-black/[0.04] text-xs font-normal uppercase text-[#8D8D8D] h-10 py-0 px-3 w-[274px] border-b border-black/[0.09] w-[274px]">
                  생성일
                </TableHead>
                <TableHead className="bg-black/[0.04] text-xs font-normal uppercase text-[#8D8D8D] h-10 py-0 px-3 border-b border-black/[0.09] w-[281px]">
                  상태
                </TableHead>
                <TableHead className="bg-black/[0.04] text-xs font-normal uppercase text-[#8D8D8D] h-10 py-0 px-3 text-right border-b border-black/[0.09]">
                  관리
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.map((agent) => (
                <TableRow
                  key={agent.id}
                  className="last:[&_td]:border-b-0 cursor-pointer"
                  onClick={() => navigate(`/my-agents/${agent.id}/detail`)}
                >
                  <TableCell className="py-0 max-w-[373px] h-[48px] border-b border-black/[0.09]">
                    <TruncatedTooltip className="block truncate text-base text-[#202020]">
                      {agent.name || "-"}
                    </TruncatedTooltip>
                  </TableCell>
                  <TableCell className="py-0 text-base text-[#202020] h-[48px] border-b border-black/[0.09]">
                    {agent.created_at ? formatDate(agent.created_at) : "-"}
                  </TableCell>
                  <TableCell className="py-0 h-[48px] border-b border-black/[0.09]">
                    {getStatusBadge(agent.status)}
                  </TableCell>
                  <TableCell className="py-0 px-3 h-[48px] border-b border-black/[0.09]">
                    <div
                      className="flex items-center justify-end gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        onClick={() => setDeleteTarget(agent.id)}
                        className="w-[80px] h-auto rounded-full bg-[rgba(245,43,0,0.09)] px-3 py-2 text-xs font-bold text-[#CF3716] hover:bg-[rgba(245,43,0,0.15)] transition-colors"
                      >
                        삭제
                      </Button>
                      {canEdit(agent.status) ? (
                        <Button
                          variant="ghost"
                          onClick={() => navigate(`/my-agents/${agent.id}/edit`)}
                          className="w-[80px] h-auto rounded-full border border-black/[0.15] bg-white/[0.01] px-3 py-2 text-xs font-bold text-[#202020] backdrop-blur-[40px] hover:bg-black/[0.04] transition-colors"
                        >
                          수정
                        </Button>
                      ) : (
                        <div className="w-[80px]" />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          classname="hidden lg:flex "
        />

        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          icon={<img src={IconHelp} alt="Icon Alert Circle" />}
          iconVariant="neutral"
          title="해당 에이전트를 삭제하시겠습니까?"
          description="에이전트와 관련 데이터가 모두 삭제됩니다"
          primaryAction={{ label: "네", onClick: handleDelete }}
          secondaryAction={{
            label: "아니요",
            onClick: () => setDeleteTarget(null),
          }}
        />
      </div>
    </>
  );
}
